import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "../styles/SyllabusPage.css";

import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

/* DATA IMPORTS */
import { SCHOOLS_SYLLABUS_DATA } from "../data/schools";
import { COLLEGES_SYLLABUS_DATA } from "../data/colleges";
import { INTERNSHIP_SYLLABUS_DATA } from "../data/internships";
import { CORPORATES_SYLLABUS_DATA } from "../data/corporates";
import { BANKING_SYLLABUS_DATA } from "../data/banking";
import { FDP_SYLLABUS_DATA } from "../data/fdp";
import { HOMEMAKERS_SYLLABUS_DATA } from "../data/homemakers";
import { MEDICALCODING_SYLLABUS_DATA } from "../data/medicalcoding";

/* =========================
   CARD
========================= */
function SyllabusCard({ course, onEnroll }) {
  return (
    <div className="syllabus-card">
      <div
        className="card-image"
        onClick={() => onEnroll(course)}
        style={{ cursor: "pointer" }}
      >
        <img src={course.image} alt={course.title} />
      </div>

      <div className="meta">
        <span className="mode">🎓 Mentor-led online or offline</span>
        <span className="class">📅 Monday – Saturday</span>
      </div>

      <div className="card-body">
        <span className="program-type">{course.category}</span>
        <h3>{course.title}</h3>
        <button className="apply-btn" onClick={() => onEnroll(course)}>
          Enroll Now
        </button>
      </div>
    </div>
  );
}

/* =========================
   PAGE
========================= */
export default function SyllabusPage() {
  const { category } = useParams();

  const allowedCategories = [
    "schools",
    "colleges",
    "corporates",
    "banking",
    "fdp",
    "homemakers",
  ];

  const [activeCategory, setActiveCategory] = useState(
    allowedCategories.includes(category) ? category : "schools"
  );

  const [collegeOpen, setCollegeOpen] = useState(category === "colleges");
  const [collegeSub, setCollegeSub] = useState("courses");

  const [demoCourse, setDemoCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [visitorName, setVisitorName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [visitorMessage, setVisitorMessage] = useState("");
  const [orgOrInstitution, setOrgOrInstitution] = useState("");
  const [bookingStatus, setBookingStatus] = useState(null);

  /* SYNC URL */
  useEffect(() => {
    if (allowedCategories.includes(category)) {
      setActiveCategory(category);
      setCollegeOpen(category === "colleges");
    } else {
      setActiveCategory("schools");
      setCollegeOpen(false);
    }
  }, [category]);

  /* SCROLL FIX */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeCategory, collegeSub]);

  /* GET COURSES */
  const getActiveCourses = () => {
    let courses = [];

    if (activeCategory === "schools") courses = SCHOOLS_SYLLABUS_DATA;

    if (activeCategory === "colleges") {
      courses =
        collegeSub === "internships"
          ? INTERNSHIP_SYLLABUS_DATA
          : COLLEGES_SYLLABUS_DATA;
    }

    if (activeCategory === "corporates") courses = CORPORATES_SYLLABUS_DATA;
    if (activeCategory === "banking") courses = BANKING_SYLLABUS_DATA;
    if (activeCategory === "fdp") courses = FDP_SYLLABUS_DATA;
    if (activeCategory === "homemakers") courses = HOMEMAKERS_SYLLABUS_DATA;
    if (activeCategory === "medicalcoding") courses = MEDICALCODING_SYLLABUS_DATA;

    if (!searchQuery.trim()) return courses;

    const q = searchQuery.toLowerCase();

    return courses.filter(
      (course) =>
        course.title?.toLowerCase().includes(q) ||
        course.modules?.some((m) => m.toLowerCase().includes(q)) ||
        course.outcomes?.some((o) => o.toLowerCase().includes(q))
    );
  };

  /* ESC CLOSE */
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setDemoCourse(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* RESET FORM */
  useEffect(() => {
    if (demoCourse) {
      setVisitorName("");
      setVisitorEmail("");
      setVisitorPhone("");
      setVisitorMessage("");
      setOrgOrInstitution("");
      setBookingStatus(null);
    }
  }, [demoCourse]);

  /* CONFIRM BOOKING */
  async function confirmBooking(e) {
    e.preventDefault();

    if (!visitorName || !visitorEmail || !visitorPhone) {
      setBookingStatus({ type: "error", message: "Fill all details." });
      return;
    }

    if (
      (demoCourse.category === "fdp" ||
        demoCourse.category === "corporates") &&
      !orgOrInstitution
    ) {
      setBookingStatus({
        type: "error",
        message:
          demoCourse.category === "fdp"
            ? "Please enter institution / organization name."
            : "Please enter organization name.",
      });
      return;
    }

    setBookingStatus({ type: "loading", message: "Booking..." });

    try {
      await addDoc(collection(db, "demoBookings"), {
        name: visitorName,
        email: visitorEmail,
        phone: visitorPhone,
        course: demoCourse.title,
        category: demoCourse.category,
        institutionOrOrganization:
          demoCourse.category === "fdp" ||
          demoCourse.category === "corporates"
            ? orgOrInstitution
            : null,
        message: visitorMessage,
        createdAt: serverTimestamp(),
      });

      setBookingStatus({
        type: "success",
        message: "Booking confirmed! We’ll contact you shortly.",
      });

      setTimeout(() => setDemoCourse(null), 1500);
    } catch (err) {
      console.error(err);
      setBookingStatus({
        type: "error",
        message: "Booking failed. Please try again.",
      });
    }
  }

  return (
    <div className="syllabus-page">
      <div className="syllabus-layout">
        <aside className="sidebar">
          {[
            { key: "schools", label: "SCHOOL STUDENTS" },
            { key: "colleges", label: "COLLEGE STUDENTS" },
            { key: "fdp", label: "FACULTY DEVELOPMENT PROGRAM" },
            { key: "banking", label: "BANKING ASPIRANTS" },
            { key: "corporates", label: "CORPORATE EMPLOYEES" },
            { key: "homemakers", label: "HOME MAKERS" },
            { key: "medicalcoding", label: "MEDICAL CODING" },
          ].map((item) => (
            <React.Fragment key={item.key}>
              <div
                className={`sidebar-item ${
                  activeCategory === item.key ? "active" : ""
                }`}
                onClick={() => {
                  setActiveCategory(item.key);
                  setCollegeOpen(item.key === "colleges");
                }}
              >
                {item.label}
              </div>

              {item.key === "colleges" && collegeOpen && (
                <div className="sidebar-submenu">
                  {[
                    { key: "courses", label: "COURSES" },
                    { key: "internships", label: "INTERNSHIPS & PROJECTS" },
                  ].map(({ key, label }) => (
                    <div
                      key={key}
                      className={`sidebar-subitem ${
                        collegeSub === key ? "active" : ""
                      }`}
                      onClick={() => setCollegeSub(key)}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </aside>

        <section className="syllabus-grid-wrapper">
          <div className="syllabus-search">
            <input
              type="text"
              placeholder="Search courses, modules, outcomes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <section className="syllabus-grid">
            {getActiveCourses().map((course) => (
              <SyllabusCard
                key={course.id}
                course={{ ...course, category: activeCategory }}
                onEnroll={setDemoCourse}
              />
            ))}
          </section>
        </section>
      </div>

      {demoCourse && (
        <div className="demo-overlay" onClick={() => setDemoCourse(null)}>
          <form
            className="demo-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={confirmBooking}
          >
            <h2>Demo — {demoCourse.title}</h2>

            <div className="demo-modal-content">
              <div className="demo-left">
                <strong>
                  {demoCourse.title} ({demoCourse.category})
                </strong>

                {demoCourse.modules && (
                  <>
                    <h4>What You Learn</h4>
                    <ul className="module-list">
                      {demoCourse.modules.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="demo-right">
                <input
                  placeholder="Your name"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                />
                <input
                  placeholder="Phone number"
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value)}
                />
                <input
                  placeholder="Email address"
                  value={visitorEmail}
                  onChange={(e) => setVisitorEmail(e.target.value)}
                />

                <textarea
                  className="demo-textarea"
                  placeholder="Your message / requirements"
                  value={visitorMessage}
                  onChange={(e) => setVisitorMessage(e.target.value)}
                />

                {bookingStatus && (
                  <p className={`status ${bookingStatus.type}`}>
                    {bookingStatus.message}
                  </p>
                )}
              </div>
            </div>

            <div className="demo-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setDemoCourse(null)}
              >
                Cancel
              </button>
              <button type="submit" className="apply-btn">
                Confirm Booking
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}